// repoToWorkflow — turn a filtered file-path list into a canvas-ready
// MultiSheetWorkflowState using the cartographer rules.
//
// Two modes:
//   - 'single' : 1 sheet with a central system block ringed by
//                subsystem groups (folder-buckets become blocks).
//   - 'multi'  : ~4–7 sheets, one per subsystem, ~4–6 groups per sheet
//                (clustered by folder bucket), ~5 blocks per group.
//   - 'auto'   : single when files.length < 50, otherwise multi.
//
// Output ALWAYS contains connectors so the resulting canvas reads as a
// graph, not a pin-board. Single-sheet wires the system block to every
// subsystem representative + sequentially chains files inside each
// group. Multi-sheet adds cross-sheet connectors between adjacent
// sheets' representative blocks.
//
// Pure module — no React, no I/O. All coordinates snap to a 20-unit
// grid (project rule). Block (x, y) is the CENTER of the block.

import type {
  MultiSheetWorkflowState,
  WorkflowSheet,
  WorkflowBlock,
  BlockGroup,
  Connector,
  CrossSheetConnector,
} from '@/types/workflow';
import { classifyFile } from './cartographerOverview';
import {
  CARTOGRAPHER_PALETTE,
  CARTOGRAPHER_ORDER,
  DETAIL_LEVELS,
  LAYOUT_GRID,
  AUTO_SINGLE_SHEET_MAX_FILES,
  snapToGrid,
  type DetailLevelKey,
} from '@/lib/cartographer/spec';

export type RepoScanMode = 'single' | 'multi' | 'auto';
export type RepoDetailLevel = DetailLevelKey;

export interface RepoToWorkflowOptions {
  mode: RepoScanMode;
  /** Display name for the produced workflow. */
  workflowName: string;
  /** Soft cap per group; folders bigger than this collapse to one block. */
  folderCollapseThreshold?: number;
  /** Hard cap on blocks per group. */
  maxBlocksPerGroup?: number;
  /** Hard cap on sheets in multi mode. */
  maxSheets?: number;
  /** How verbose blocks should be (controls density + descriptions). */
  detailLevel?: RepoDetailLevel;
  /** When true, populate block.config.detailedDescription with file listings. */
  attachCodebase?: boolean;
}

// Palette / icons / order are all imported from cartographer/spec.ts —
// the single source of truth shared with the AiJsonDialog and the
// AtlasSheetCanvas cartographer-overview engine. To change a color or
// icon casing, edit src/lib/cartographer/spec.ts ONLY.

const PALETTE: Record<string, { color: string; label: string; icon: string }> =
  Object.fromEntries(
    CARTOGRAPHER_PALETTE.map((r) => [r.key, { color: r.color, label: r.label, icon: r.iconKebab }]),
  );

const ORDER: ReadonlyArray<string> = CARTOGRAPHER_ORDER;

// Layout — multiples of 20 (canvas grid rule). Kept as local consts
// for readability; values come from LAYOUT_GRID.
const BLOCK_DX = LAYOUT_GRID.BLOCK_DX;
const STRIP_DY = LAYOUT_GRID.STRIP_DY;
const STRIP_INNER_DY = LAYOUT_GRID.STRIP_INNER_DY;
const SYSTEM_X = LAYOUT_GRID.SYSTEM_X;
const SYSTEM_Y = LAYOUT_GRID.SYSTEM_Y;

function snap20(v: number): number { return snapToGrid(v); }

function detailParams(level: RepoDetailLevel): { maxBlocks: number; threshold: number } {
  const spec = DETAIL_LEVELS[level] ?? DETAIL_LEVELS.standard;
  return { maxBlocks: spec.maxBlocksPerGroup, threshold: spec.folderCollapseThreshold };
}

function folderBucket(filePath: string): string {
  const parts = filePath.split('/');
  if (parts.length >= 3) {
    const seg = parts[2].includes('.') ? '' : parts[2];
    return `${parts[0]}/${parts[1]}${seg ? '/' + seg : ''}`;
  }
  if (parts.length >= 2) {
    const seg = parts[1].includes('.') ? '' : parts[1];
    return `${parts[0]}${seg ? '/' + seg : ''}`;
  }
  return parts[0] || 'root';
}

function bySubsystem(files: ReadonlyArray<string>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const f of files) {
    const sub = classifyFile(f);
    if (!out.has(sub)) out.set(sub, []);
    out.get(sub)!.push(f);
  }
  const misc = out.get('misc') ?? [];
  for (const [k, list] of Array.from(out.entries())) {
    if (k === 'misc') continue;
    if (list.length < 3) {
      misc.push(...list);
      out.delete(k);
    }
  }
  if (misc.length > 0) out.set('misc', misc);
  return out;
}

interface BlockSeed {
  id: string;
  label: string;
  description: string;
  detailed?: string;
  color: string;
  icon: string;
}

function safeId(s: string): string {
  return s.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase().replace(/^_+|_+$/g, '').slice(0, 60);
}

function makeFolderBlock(
  folder: string,
  files: string[],
  meta: { color: string; icon: string },
  attachCodebase: boolean,
): BlockSeed {
  return {
    id: `b_${safeId(folder)}__folder`,
    label: `${folder}/`,
    description: `${files.length} ${files.length === 1 ? 'file' : 'files'}`,
    detailed: attachCodebase ? files.slice(0, 50).join('\n') + (files.length > 50 ? `\n… +${files.length - 50} more` : '') : undefined,
    color: meta.color,
    icon: meta.icon,
  };
}

function makeFileBlock(
  file: string,
  meta: { color: string; icon: string },
  attachCodebase: boolean,
): BlockSeed {
  return {
    id: `b_${safeId(file)}`,
    label: file.split('/').pop() ?? file,
    description: file,
    detailed: attachCodebase ? `Path: ${file}` : undefined,
    color: meta.color,
    icon: meta.icon,
  };
}

function pickBlocks(
  files: string[],
  meta: { color: string; icon: string },
  maxBlocks: number,
  threshold: number,
  attachCodebase: boolean,
): BlockSeed[] {
  const byFolder = new Map<string, string[]>();
  for (const f of files) {
    const k = folderBucket(f);
    if (!byFolder.has(k)) byFolder.set(k, []);
    byFolder.get(k)!.push(f);
  }
  const folders = [...byFolder.entries()].sort((a, b) => b[1].length - a[1].length);

  const out: BlockSeed[] = [];
  for (const [folder, list] of folders) {
    if (out.length >= maxBlocks) break;
    if (list.length >= threshold) {
      out.push(makeFolderBlock(folder, list, meta, attachCodebase));
    } else {
      for (const f of list) {
        if (out.length >= maxBlocks) break;
        out.push(makeFileBlock(f, meta, attachCodebase));
      }
    }
  }
  return out;
}

function toWorkflowBlock(seed: BlockSeed, x: number, y: number, suffix: string): WorkflowBlock {
  return {
    id: `${seed.id}_${suffix}`.slice(0, 80),
    type: 'general',
    x: snap20(x),
    y: snap20(y),
    color: seed.color,
    config: {
      name: seed.label,
      description: seed.description,
      ...(seed.detailed ? { detailedDescription: seed.detailed } : {}),
      icon: seed.icon,
    },
  };
}

// ── Ring layout for single-sheet ───────────────────────────────────────
//
// Subsystems are placed in a balanced grid above + below the system
// block. Each subsystem strip lays its blocks left→right, wrapping
// after 6 columns.

function ringPositionsForCount(n: number): Array<{ row: number; col: number }> {
  // 1 below center per subsystem; arrange in 2 columns × ceil(n/2) rows.
  const positions: Array<{ row: number; col: number }> = [];
  // Two-column zigzag: even index left, odd index right of system.
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / 2);
    const col = i % 2 === 0 ? -1 : 1;
    positions.push({ row, col });
  }
  return positions;
}

function buildSingleSheet(
  buckets: Map<string, string[]>,
  workflowName: string,
  threshold: number,
  maxBlocks: number,
  attachCodebase: boolean,
  repoName: string,
): MultiSheetWorkflowState {
  const orderedSubs = ORDER.filter((s) => buckets.has(s));
  const positions = ringPositionsForCount(orderedSubs.length);

  const blocks: WorkflowBlock[] = [];
  const groups: BlockGroup[] = [];
  const connectors: Connector[] = [];

  // 1. System root block
  const systemBlock: WorkflowBlock = {
    id: 'b_system_root',
    type: 'general',
    x: snap20(SYSTEM_X),
    y: snap20(SYSTEM_Y),
    color: '#1a2e5a',
    config: {
      name: repoName,
      description: `Repository · ${[...buckets.values()].reduce((a, l) => a + l.length, 0)} files`,
      detailedDescription: attachCodebase
        ? `Subsystems detected: ${orderedSubs.join(', ')}`
        : undefined,
      icon: 'box',
    },
  };
  blocks.push(systemBlock);

  // 2. Subsystem strips
  orderedSubs.forEach((sub, i) => {
    const meta = PALETTE[sub];
    const files = buckets.get(sub) ?? [];
    const seeds = pickBlocks(files, meta, maxBlocks, threshold, attachCodebase);
    if (seeds.length === 0) return;

    const pos = positions[i];
    const baseY = snap20(SYSTEM_Y + 360 + pos.row * STRIP_DY);
    const colsPerRow = 4;
    const stripWidth = Math.min(seeds.length, colsPerRow) * BLOCK_DX;
    const baseX = snap20(pos.col * (stripWidth / 2 + 320));

    const seenIds = new Set<string>();
    const blockIds: string[] = [];
    const stripBlocks: WorkflowBlock[] = seeds.map((seed, j) => {
      const col = j % colsPerRow;
      const row = Math.floor(j / colsPerRow);
      const x = baseX - (stripWidth / 2) + col * BLOCK_DX + BLOCK_DX / 2;
      const y = baseY + row * STRIP_INNER_DY;
      let blk = toWorkflowBlock(seed, x, y, `s0_${i}_${j}`);
      while (seenIds.has(blk.id)) blk = { ...blk, id: `${blk.id}_x` };
      seenIds.add(blk.id);
      blockIds.push(blk.id);
      return blk;
    });
    blocks.push(...stripBlocks);

    groups.push({
      id: `g_${sub}_s0`,
      blockIds,
      color: meta.color,
      name: `${meta.label} · ${files.length}`,
      collapsed: false,
    });

    // Connectors: system → first block of strip
    connectors.push({
      id: `c_root_${sub}`,
      fromId: systemBlock.id,
      toId: stripBlocks[0].id,
      fromPort: pos.row === 0 ? (pos.col < 0 ? 'bottom-left' : 'bottom-right') : 'bottom',
      toPort: 'top',
      color: meta.color,
    });

    // Sequential connectors within strip
    for (let k = 0; k < stripBlocks.length - 1; k++) {
      connectors.push({
        id: `c_${sub}_${k}`,
        fromId: stripBlocks[k].id,
        toId: stripBlocks[k + 1].id,
        fromPort: 'right',
        toPort: 'left',
        color: meta.color,
      });
    }
  });

  return {
    name: workflowName,
    sheets: [{
      id: 'sheet-architecture',
      name: 'Architecture Overview',
      blocks,
      connectors,
      groups,
    }],
    crossSheetConnectors: [],
    activeSheetIndex: 0,
    coordinateOrigin: 'center',
  };
}

function buildMultiSheet(
  buckets: Map<string, string[]>,
  workflowName: string,
  threshold: number,
  maxBlocks: number,
  maxSheets: number,
  attachCodebase: boolean,
): MultiSheetWorkflowState {
  const sheets: WorkflowSheet[] = [];
  const crossSheetConnectors: CrossSheetConnector[] = [];
  const ordered = ORDER.filter((s) => buckets.has(s));
  const usable = ordered.slice(0, maxSheets);

  // Track first block id per sheet for cross-sheet connectors.
  const firstBlockPerSheet: string[] = [];

  usable.forEach((sub, sheetIdx) => {
    const files = buckets.get(sub) ?? [];
    const meta = PALETTE[sub];

    const byFolder = new Map<string, string[]>();
    for (const f of files) {
      const k = folderBucket(f);
      if (!byFolder.has(k)) byFolder.set(k, []);
      byFolder.get(k)!.push(f);
    }
    const folderEntries = [...byFolder.entries()].sort((a, b) => b[1].length - a[1].length);

    const sheetGroupCap = 6;
    const grouped = folderEntries.slice(0, sheetGroupCap);
    const leftover = folderEntries.slice(sheetGroupCap).flatMap(([, list]) => list);

    const sheetBlocks: WorkflowBlock[] = [];
    const sheetGroups: BlockGroup[] = [];
    const sheetConnectors: Connector[] = [];

    grouped.forEach(([folder, list], gIdx) => {
      const subset = list.slice(0, maxBlocks);
      const seeds: BlockSeed[] = list.length >= threshold
        ? [makeFolderBlock(folder, list, meta, attachCodebase)]
        : subset.map((f) => makeFileBlock(f, meta, attachCodebase));

      const baseY = snap20(-360 + gIdx * STRIP_DY);
      const colsPerRow = 6;

      const seen = new Set<string>();
      const blockIds: string[] = [];
      const builtBlocks: WorkflowBlock[] = seeds.map((seed, j) => {
        const col = j % colsPerRow;
        const row = Math.floor(j / colsPerRow);
        const x = -((Math.min(seeds.length, colsPerRow) * BLOCK_DX) / 2) + col * BLOCK_DX + BLOCK_DX / 2;
        const y = baseY + row * STRIP_INNER_DY;
        let blk = toWorkflowBlock(seed, x, y, `s${sheetIdx}_g${gIdx}_${j}`);
        while (seen.has(blk.id)) blk = { ...blk, id: `${blk.id}_x` };
        seen.add(blk.id);
        blockIds.push(blk.id);
        return blk;
      });
      sheetBlocks.push(...builtBlocks);

      sheetGroups.push({
        id: `g_${sub}_${gIdx}_s${sheetIdx}`,
        blockIds,
        color: meta.color,
        name: `${folder} · ${list.length}`,
        collapsed: false,
      });

      // Chain blocks inside group
      for (let k = 0; k < builtBlocks.length - 1; k++) {
        sheetConnectors.push({
          id: `c_${sub}_${gIdx}_${k}_s${sheetIdx}`,
          fromId: builtBlocks[k].id,
          toId: builtBlocks[k + 1].id,
          fromPort: 'right',
          toPort: 'left',
          color: meta.color,
        });
      }
    });

    // Chain groups: first block of group N → first block of group N+1
    const groupReps = sheetGroups.map((g) => g.blockIds[0]).filter(Boolean);
    for (let k = 0; k < groupReps.length - 1; k++) {
      sheetConnectors.push({
        id: `c_grouplink_${k}_s${sheetIdx}`,
        fromId: groupReps[k],
        toId: groupReps[k + 1],
        fromPort: 'bottom',
        toPort: 'top',
        color: meta.color,
      });
    }

    if (leftover.length > 0) {
      const baseY = snap20(-360 + grouped.length * STRIP_DY);
      const id = `b_more_s${sheetIdx}`;
      const block: WorkflowBlock = {
        id,
        type: 'general',
        x: snap20(-((BLOCK_DX) / 2)),
        y: snap20(baseY),
        color: meta.color,
        config: {
          name: `+${leftover.length} more files`,
          description: leftover.slice(0, 8).join(', '),
          ...(attachCodebase ? { detailedDescription: leftover.join('\n') } : {}),
          icon: meta.icon,
        },
      };
      sheetBlocks.push(block);
      sheetGroups.push({
        id: `g_more_s${sheetIdx}`,
        blockIds: [id],
        color: meta.color,
        name: `+${leftover.length} more`,
        collapsed: false,
      });
    }

    sheets.push({
      id: `sheet-${sub}-${sheetIdx}`,
      name: `${String.fromCodePoint(0x2460 + Math.min(sheetIdx, 19))} ${meta.label}`,
      blocks: sheetBlocks,
      connectors: sheetConnectors,
      groups: sheetGroups,
    });

    firstBlockPerSheet.push(sheetBlocks[0]?.id ?? '');
  });

  // Cross-sheet connectors between adjacent sheets.
  for (let i = 0; i < sheets.length - 1; i++) {
    const fromId = firstBlockPerSheet[i];
    const toId = firstBlockPerSheet[i + 1];
    if (!fromId || !toId) continue;
    crossSheetConnectors.push({
      id: `csc_${i}_${i + 1}`,
      fromSheetIndex: i,
      toSheetIndex: i + 1,
      fromBlockId: fromId,
      toBlockId: toId,
      fromPort: 'right',
      toPort: 'left',
    });
  }

  return {
    name: workflowName,
    sheets: sheets.length > 0 ? sheets : [{
      id: 'sheet-empty',
      name: 'Empty',
      blocks: [],
      connectors: [],
      groups: [],
    }],
    crossSheetConnectors,
    activeSheetIndex: 0,
    coordinateOrigin: 'center',
  };
}

/**
 * Entry point — feed file paths + a mode, get a canvas-ready workflow.
 */
export function repoToWorkflow(
  files: ReadonlyArray<string>,
  options: RepoToWorkflowOptions,
): MultiSheetWorkflowState {
  const detail = detailParams(options.detailLevel ?? 'standard');
  const threshold = options.folderCollapseThreshold ?? detail.threshold;
  const maxBlocks = options.maxBlocksPerGroup ?? detail.maxBlocks;
  const maxSheets = options.maxSheets ?? 10;
  const attachCodebase = options.attachCodebase ?? false;
  const buckets = bySubsystem(files);

  const effectiveMode: 'single' | 'multi' =
    options.mode === 'auto'
      ? (files.length < AUTO_SINGLE_SHEET_MAX_FILES ? 'single' : 'multi')
      : options.mode;

  if (effectiveMode === 'single') {
    return buildSingleSheet(buckets, options.workflowName, threshold, maxBlocks, attachCodebase, options.workflowName);
  }
  return buildMultiSheet(buckets, options.workflowName, threshold, maxBlocks, maxSheets, attachCodebase);
}

export const __repoToWorkflowInternals = {
  PALETTE,
  ORDER,
  bySubsystem,
  folderBucket,
};
